// src/utils/projectLoadedCost.js
// ─────────────────────────────────────────────────────────────────────────
// Builds the per-project loaded-cost records that the Project
// Profitability page already computes inline (and that other consumers
// — Inquiries Fee Intelligence — want to read without re-running the
// full pipeline). Output is shaped for upsert_project_loaded_costs.
//
// The same primitives as ProjectProfitabilityPage:
//   - direct labour from computeProjectLabour with calcMonthlyTrueCost
//     (unmarked-up: the activity-overhead engine adds the overhead
//     pool on top, so multiplying labour by markup here would
//     double-count)
//   - direct expenses (sum of activeExpenses for the project,
//     excluding planned-status placeholders)
//   - activity-allocated overhead = operationalOH + dormancy +
//     internal-project redistribution + salary-gap allocation
//
// All amounts are in INR (the base currency); the consumer page
// converts to display currency as needed.
//
// Keep this file dependency-free of React state; pass everything in.
// ─────────────────────────────────────────────────────────────────────────
import { computeProjectLabour, computeInternalCostRedistribution } from './projectLabour';
import { computeActivityOverheadEngine } from './activityOverheadEngine';

const toNumericIdLocal = (id) => {
  if (typeof id === 'number') return id;
  if (typeof id === 'string' && /^-?\d+$/.test(id.trim())) return parseInt(id, 10);
  return id;
};

/**
 * Build per-project labour + expenses indices from time entries / expenses.
 */
function buildIndices({ activeTimeEntries, activeExpenses }) {
  const byProject = {};
  const byUserMonth = {};
  const monthSet = new Set();
  for (const e of (activeTimeEntries || [])) {
    if (!e?.date || !e.projectId || !e.userId) continue;
    const ymKey = String(e.date).substring(0, 7);
    const hours = (e.duration || 0) / 3600;
    if (!byProject[e.projectId]) byProject[e.projectId] = {};
    if (!byProject[e.projectId][e.userId]) byProject[e.projectId][e.userId] = {};
    byProject[e.projectId][e.userId][ymKey] = (byProject[e.projectId][e.userId][ymKey] || 0) + hours;
    if (!byUserMonth[e.userId]) byUserMonth[e.userId] = {};
    byUserMonth[e.userId][ymKey] = (byUserMonth[e.userId][ymKey] || 0) + hours;
    monthSet.add(ymKey);
  }

  const expensesByProject = {};
  for (const ex of (activeExpenses || [])) {
    if (!ex?.projectId) continue;
    if (ex.status === 'planned') continue;
    expensesByProject[ex.projectId] = (expensesByProject[ex.projectId] || 0) + (Number(ex.amount) || 0);
  }
  return { byProject, byUserMonth, monthSet, expensesByProject };
}

/**
 * Mirror of the salary-gap memo in ProjectProfitabilityPage: per
 * user-month, the cash labour cost that wasn't logged to any project.
 * Returned as { [office]: { [ym]: gapInr } } so the activity-overhead
 * engine can distribute it to projects by hour share.
 */
function buildSalaryGapByOfficeMonth({
  activeUsers, fullSalaryLedger, byUserMonth,
  calcMonthlyTrueCost, getEffectiveContractedHours,
}) {
  const byOfficeMonth = {};
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  for (const u of (activeUsers || [])) {
    const auth = u.authUserId;
    if (!auth) continue;
    const userMonths = fullSalaryLedger?.[auth];
    if (!userMonths) continue;
    const office = (u.officeLocation || 'NL') === 'IN' ? 'IN' : 'NL';
    for (const [ymKey, row] of Object.entries(userMonths)) {
      if (ymKey > currentMonthKey) continue;
      const baseSalary = Number(row?.base_salary) || 0;
      const bonus = Number(row?.bonus) || 0;
      if (baseSalary <= 0 && bonus <= 0) continue;
      const m = ymKey.match(/^(\d{4})-(\d{1,2})$/);
      if (!m) continue;
      const yr = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10);
      const cost = calcMonthlyTrueCost(u.id, yr, mo);
      const baseCH = cost.contractedHours || 176;
      const proratedCost = (cost.totalCost || 0) * baseCH / 176;
      if (proratedCost <= 0) continue;
      const totalLogged = byUserMonth?.[u.id]?.[ymKey] || 0;
      const effCH = getEffectiveContractedHours
        ? getEffectiveContractedHours(u.id, yr, mo)
        : baseCH;
      const normalized = Math.max(totalLogged, effCH);
      if (normalized <= 0) continue;
      const gap = proratedCost * Math.max(0, normalized - totalLogged) / normalized;
      if (gap <= 0) continue;
      if (!byOfficeMonth[office]) byOfficeMonth[office] = {};
      byOfficeMonth[office][ymKey] = (byOfficeMonth[office][ymKey] || 0) + gap;
    }
  }
  return byOfficeMonth;
}

function buildOHIndices({ monthlyOfficeOverheads, activeOfficeOverheads, isEmployerBurden }) {
  const monthlyOHIndex = {};
  for (const o of (monthlyOfficeOverheads || [])) {
    if (isEmployerBurden && isEmployerBurden(o.category)) continue;
    const loc = (o.officeLocation || 'NL') === 'IN' ? 'IN' : 'NL';
    const key = `${loc}|${o.month}`;
    monthlyOHIndex[key] = (monthlyOHIndex[key] || 0) + (o.amountInr ?? 0);
  }
  const yearlyOHIndex = {};
  for (const o of (activeOfficeOverheads || [])) {
    if (isEmployerBurden && isEmployerBurden(o.category)) continue;
    const loc = (o.officeLocation || 'NL') === 'IN' ? 'IN' : 'NL';
    if (!yearlyOHIndex[loc]) yearlyOHIndex[loc] = {};
    yearlyOHIndex[loc][o.year] = (yearlyOHIndex[loc][o.year] || 0) + (o.amountInInr ?? o.amount ?? 0);
  }
  return { monthlyOHIndex, yearlyOHIndex };
}

function buildInternalProjectIds({ activeProjects, activeClients }) {
  const clientMap = {};
  (activeClients || []).forEach(c => { clientMap[toNumericIdLocal(c.id)] = c; });
  const ids = new Set();
  (activeProjects || []).forEach(p => {
    const client = clientMap[toNumericIdLocal(p.clientId)];
    if (!client || !p.clientId || (client.name || '').toLowerCase() === 'brio') {
      ids.add(toNumericIdLocal(p.id));
    }
  });
  return ids;
}

/**
 * Main entry point. Returns an array of records ready for the
 * upsert_project_loaded_costs RPC.
 *
 * Inputs (all required unless marked optional):
 *   activeTimeEntries, activeExpenses, activeUsers, activeProjects,
 *   activeClients, monthlyOfficeOverheads, activeOfficeOverheads,
 *   fullSalaryLedger,
 *   calcMonthlyTrueCost, getEffectiveContractedHours,
 *   getUserProfileAtDate, isEmployerBurden,
 *   projectStartYearByProjectId   { [pid]: year } — optional, used to
 *     denormalise the year for the inflation lookup
 *   projectCountryCodeByProjectId { [pid]: 'IN'|'NL'|null } — optional
 *   DORMANCY_POOL_PCT             — optional (defaults to 0.03)
 */
export function computeProjectLoadedCostRecords(inputs) {
  const {
    activeTimeEntries, activeExpenses, activeUsers, activeProjects, activeClients,
    monthlyOfficeOverheads, activeOfficeOverheads, fullSalaryLedger,
    calcMonthlyTrueCost, getEffectiveContractedHours, getUserProfileAtDate,
    isEmployerBurden,
    projectStartYearByProjectId = {},
    projectCountryCodeByProjectId = {},
    DORMANCY_POOL_PCT = 0.03,
  } = inputs;

  const { byProject, byUserMonth, monthSet, expensesByProject } =
    buildIndices({ activeTimeEntries, activeExpenses });
  const months = Array.from(monthSet).sort();

  const internalProjectIds = buildInternalProjectIds({ activeProjects, activeClients });
  const { monthlyOHIndex, yearlyOHIndex } =
    buildOHIndices({ monthlyOfficeOverheads, activeOfficeOverheads, isEmployerBurden });
  const salaryGapByOfficeMonth = buildSalaryGapByOfficeMonth({
    activeUsers, fullSalaryLedger, byUserMonth,
    calcMonthlyTrueCost, getEffectiveContractedHours,
  });

  // Run the activity-overhead engine over the full month range so its
  // pool allocations match what the Profitability page shows in Total
  // mode (which is the relevant view for an Inquiries-side benchmark).
  const engine = computeActivityOverheadEngine({
    activeTimeEntries, activeUsers, activeProjects,
    internalProjectIds, monthlyOHIndex, yearlyOHIndex,
    monthlyOfficeOverheads, yearlyOfficeOverheads: activeOfficeOverheads,
    DORMANCY_POOL_PCT,
    salaryGapByOfficeMonth,
  });
  const activityByProjectMonth = engine.byProjectMonth || {};
  const dormancyByProjectMonth = engine.dormancyByProjectMonth || {};
  const salaryGapByProjectMonth = engine.salaryGapByProjectMonth || {};
  const internalCostByProjectMonth = computeInternalCostRedistribution({
    byProject, byUserMonth, activeUsers, internalProjectIds,
    billableOfficeHours: engine.billableOfficeHours,
    projOfficeHours: engine.projOfficeHours,
    getUserProfileAtDate, getEffectiveContractedHours,
    calcMonthlyCost: calcMonthlyTrueCost,
  });

  const projectMap = {};
  (activeProjects || []).forEach(p => { projectMap[toNumericIdLocal(p.id)] = p; });

  // Union: projects that have time entries OR expenses (rest don't move
  // the metric so skip).
  const candidatePids = new Set();
  Object.keys(byProject).forEach(pid => candidatePids.add(pid));
  Object.keys(expensesByProject).forEach(pid => candidatePids.add(pid));

  const records = [];
  for (const pidKey of candidatePids) {
    const pidNum = toNumericIdLocal(pidKey);
    const project = projectMap[pidNum];

    let labour = 0;
    if (byProject[pidKey]) {
      const lb = computeProjectLabour({
        byProject, byUserMonth, projectId: pidKey, months,
        getUserProfileAtDate, getEffectiveContractedHours,
        calcMonthlyCost: calcMonthlyTrueCost,
      });
      labour = lb.totalAllocated || 0;
    }
    const expenses = expensesByProject[pidKey] || expensesByProject[pidNum] || 0;

    // Sum monthly overhead contributions across all months.
    let operationalOH = 0;
    let dormancyOH = 0;
    let internalOH = 0;
    let salaryGapOH = 0;
    const sumMonths = (map) => {
      const monthMap = map?.[pidKey] || map?.[pidNum];
      if (!monthMap) return 0;
      let s = 0;
      for (const v of Object.values(monthMap)) s += (Number(v) || 0);
      return s;
    };
    operationalOH = sumMonths(activityByProjectMonth);
    dormancyOH   = sumMonths(dormancyByProjectMonth);
    internalOH   = sumMonths(internalCostByProjectMonth);
    salaryGapOH  = sumMonths(salaryGapByProjectMonth);
    const activityOverhead = operationalOH + dormancyOH + internalOH + salaryGapOH;

    const total = labour + expenses + activityOverhead;
    if (total <= 0 && labour <= 0 && expenses <= 0) continue;

    records.push({
      projectId: pidNum,
      directLabourInr:     Math.round(labour * 100) / 100,
      directExpensesInr:   Math.round(expenses * 100) / 100,
      activityOverheadInr: Math.round(activityOverhead * 100) / 100,
      totalLoadedCostInr:  Math.round(total * 100) / 100,
      projectSize:          project?.projectSize ?? project?.project_size ?? null,
      projectCountryCode:   projectCountryCodeByProjectId[pidNum] || null,
      projectStartYear:     projectStartYearByProjectId[pidNum] || null,
    });
  }
  return records;
}
